import React, { useCallback, useMemo, useState } from 'react'

import { faEnvelope } from '@fortawesome/pro-regular-svg-icons'
import { Button } from 'antd'
import { Modal, Select, Text, VStack } from 'govwell-ui'
import { SelectOption } from 'govwell-ui/components/Select/types'
import { AicaEmailDigestFrequency, UserDataFragment } from 'types/graphql'

import StaffUserSelect from 'src/components/Selects/StaffUserSelect'

import { DIGEST_FREQUENCY_OPTIONS } from './digestConstants'

type Props = {
  onClose: () => void
  onAdd: (userId: number, frequency: AicaEmailDigestFrequency) => Promise<void>
  existingUserIds: number[]
  isLoading: boolean
}

const getOptionKey = (option: SelectOption<AicaEmailDigestFrequency>) => option.value

const AddDigestSubscriberModal = ({ onClose, onAdd, existingUserIds, isLoading }: Props) => {
  const [selectedUser, setSelectedUser] = useState<UserDataFragment | undefined | null>(undefined)
  const [frequency, setFrequency] = useState<AicaEmailDigestFrequency>('Weekly')

  const handleUserChange = useCallback((user: UserDataFragment | undefined | null) => {
    setSelectedUser(user)
  }, [])

  const selectedFrequencyOption = useMemo(
    () => DIGEST_FREQUENCY_OPTIONS.find((o) => o.value === frequency),
    [frequency]
  )

  const handleFrequencyChange = useCallback(
    (option: SelectOption<AicaEmailDigestFrequency> | undefined) => {
      if (option) {
        setFrequency(option.value)
      }
    },
    []
  )

  const handleSubmit = useCallback(async () => {
    if (!selectedUser) {
      return
    }
    await onAdd(selectedUser.id, frequency)
  }, [selectedUser, frequency, onAdd])

  const isSubmitDisabled = useMemo(() => {
    if (!selectedUser || isLoading) {
      return true
    }
    return existingUserIds.includes(selectedUser.id)
  }, [selectedUser, isLoading, existingUserIds])

  const showAlreadySubscribedWarning = useMemo(() => {
    return selectedUser && existingUserIds.includes(selectedUser.id)
  }, [selectedUser, existingUserIds])

  return (
    <Modal onClose={onClose}>
      <Modal.Header icon={faEnvelope}>Add Digest Subscriber</Modal.Header>
      <Modal.Content>
        <VStack gap={4}>
          <Text.Body>
            Select a team member to receive automated AICA email digests. They will receive regular
            updates showing the value the Community Assistant is providing.
          </Text.Body>
          <StaffUserSelect
            type="AnyUserInOrganization"
            label="Select User"
            placeholder="Search for users to add as subscriber"
            value={selectedUser}
            onValueChange={handleUserChange}
            isClearable
            isMultiSelect={false}
          />
          {showAlreadySubscribedWarning && (
            <Text.Body className="text-orange-600">
              This user is already subscribed to the digest.
            </Text.Body>
          )}
          <Select
            label="Digest Frequency"
            options={DIGEST_FREQUENCY_OPTIONS}
            selectedOption={selectedFrequencyOption}
            onSelectedOptionChange={handleFrequencyChange}
            getOptionKey={getOptionKey}
          />
        </VStack>
      </Modal.Content>
      <Modal.Footer>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          loading={isLoading}
        >
          Add subscriber
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default React.memo(AddDigestSubscriberModal)
